import { PAYPAL_CHECKOUT_PAY_PUBLIC_ENDPOINT } from '@app/pages/api/constants';
import { isLoggablePaymentError } from '@app/pages/check-out/utils/loggableErrors';
import logger from '@redbubble/next/logger';
import { getHost } from '@redbubble/utils/getHost';
import type { PayPalCheckoutTokenizationOptions } from 'braintree-web/paypal-checkout';
import { ErrorCodes, getPaymentErrorMessage } from '../paymentErrorLookup';
import {
  AuthorizationResponse,
  PageType,
  PayPalCheckout,
  PayPalErrorCallback,
  PaypalSdkAddress,
} from '../types';
export interface HandleOnApproveParams {
  paypalClient: PayPalCheckout;
  paymentData: PayPalCheckoutTokenizationOptions;
  rbEmail: string | null;
  clientDeviceData: string;
  onError?: PayPalErrorCallback;
  page: PageType;
  shippingOverride?: AddressDetails;
  billingOverride?: AddressDetails;
  consent?: boolean;
}

export interface ApproveResult {
  thankYouPageUrl: string | null;
  authResponse: AuthorizationResponse;
}

export interface AddressDetails {
  name: string;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    country_code: string | null;
  };
}

interface FullAddressDetails extends AddressDetails {
  phone_number: string;
}

export interface PayRequest {
  nonce: string;
  client_device_data: string;
  order_details: {
    email: string;
    consent?: boolean;
    shipping_details: FullAddressDetails;
    billing_details: FullAddressDetails;
  };
  page: PageType;
}

export interface PaySuccessPayload {
  data: {
    thanks_page_url: string;
  };
}

export interface PayErrorPayload {
  errors: {
    code: string;
    title: string;
  }[];
}

export type PayResponsePayload = PaySuccessPayload | PayErrorPayload;

class PayAPIError extends Error {
  payload: PayErrorPayload;

  constructor(payload: PayErrorPayload) {
    const message = payload.errors
      .map((err) => `${err.code}: ${err.title}`)
      .join(' | ');
    super(message);
    this.payload = payload;
    this.name = '';
  }
}

const toPaymentAddressFromPaypalSdkAddress = ({
  paypalAddress,
  paypalAccountDetails,
}: {
  paypalAddress?: PaypalSdkAddress;
  paypalAccountDetails: {
    firstName: string;
    lastName: string;
  };
}): AddressDetails => ({
  address: {
    line1: paypalAddress?.line1 || null,
    line2: paypalAddress?.line2 || null,
    city: paypalAddress?.city || null,
    state: paypalAddress?.state || null,
    postal_code: paypalAddress?.postalCode || null,
    country_code: paypalAddress?.countryCode || null,
  },
  name:
    paypalAddress?.recipientName ||
    `${paypalAccountDetails.firstName} ${paypalAccountDetails.lastName}`,
});

const getFullPaymentAddress = ({
  paypalAddress,
  overrideAddress,
  paypalAccountDetails,
}: {
  paypalAddress?: PaypalSdkAddress;
  overrideAddress?: AddressDetails;
  paypalAccountDetails: {
    firstName: string;
    lastName: string;
    phone?: string;
  };
}): FullAddressDetails => {
  const addressDetails =
    overrideAddress ||
    toPaymentAddressFromPaypalSdkAddress({
      paypalAddress,
      paypalAccountDetails,
    });

  return {
    ...addressDetails,
    // XXX(cy): We use the phone number that is attached to the user's account due to
    // mismatches in validation rules between the Stripe address form and BT/PP expectations.
    // Neither BT or PP provide rules for the fields, so we take the safer approach of
    // omitting the value rather than trying to validate/correct the value with guesswork.
    phone_number: paypalAccountDetails.phone || '',
    address: {
      ...addressDetails.address,
      city: addressDetails?.address.city || '-',
      state: addressDetails?.address.state || '-',
      postal_code: addressDetails?.address.postal_code || '-',
    },
  };
};

const payUrl = [getHost(), PAYPAL_CHECKOUT_PAY_PUBLIC_ENDPOINT].join('');

const doApprove = async ({
  paypalClient,
  paymentData,
  rbEmail,
  clientDeviceData,
  onError,
  page,
  shippingOverride,
  billingOverride,
  consent,
}: {
  paypalClient: PayPalCheckout;
  paymentData: PayPalCheckoutTokenizationOptions;
  rbEmail: string | null;
  clientDeviceData: string;
  onError?: PayPalErrorCallback;
  page: PageType;
  shippingOverride?: AddressDetails;
  billingOverride?: AddressDetails;
  consent?: boolean;
}): Promise<ApproveResult> => {
  const authResponse = await paypalClient
    .tokenizePayment(paymentData)
    .catch((e) => {
      const message = `Failed to tokenize payment: ${e.message}`;
      logger.error(
        {
          err: e,
          context: 'PayPalExpress/handlers/handleOnApprove',
          message,
        },
        message
      );
      onError?.(
        getPaymentErrorMessage({ code: ErrorCodes.CHARGE_FAILED_RETRY_ALLOWED })
      );

      // rethrow the error as to block doApprove from continuing,
      // and to allow the caller to handle the error, too.
      throw e;
    });

  const { details } = authResponse;
  const { billingAddress, shippingAddress } = details;

  // For Paypal payments where users can edit their shipping details
  // in the Paypal Modal (Cart and Cart Tray), we always use Redbubble
  // member email address.
  // For Paypal payments where users can't edit their shipping details
  // in the Paypal Modal (Checkout), we always use the email address
  // entered in the Delivery Form first.
  // Paypal account email is a fallback if either is not available.
  const orderEmail = rbEmail || details.email;

  const payload: PayRequest = {
    nonce: authResponse.nonce,
    client_device_data: clientDeviceData,
    order_details: {
      email: orderEmail,
      consent,
      shipping_details: getFullPaymentAddress({
        paypalAddress: shippingAddress,
        overrideAddress: shippingOverride,
        paypalAccountDetails: details,
      }),
      billing_details: getFullPaymentAddress({
        paypalAddress: billingAddress,
        overrideAddress: billingOverride,
        paypalAccountDetails: details,
      }),
    },
    page: page,
  };

  const thankYouPageUrl = await fetch(payUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
    .then(async (res) => {
      const json = (await res.json()) as PaySuccessPayload | PayErrorPayload;
      if ('errors' in json) {
        throw new PayAPIError(json);
      }

      // We should have errors in the response body if the request failed,
      // but this is to ensure we handle any unexpected responses.
      if (!res.ok) {
        throw new PayAPIError({
          errors: [
            {
              code: ErrorCodes.CHARGE_FAILED,
              title: `Pay API request failed with status ${res.status} and unknown error "${res.statusText}"`,
            },
          ],
        });
      }

      return json.data.thanks_page_url;
    })
    .catch((e) => {
      // An error will come from code we control (the Monolith API return payload)
      // or code we don't control (such as OS-level network errors).

      // The former satisfies the "PayAPIError" schema, meaning we can parse it to
      // determine if the error is something worth logging to Datadog or not.
      const loggableErrors =
        e instanceof PayAPIError
          ? e.payload.errors.filter(isLoggablePaymentError)
          : [e];
      if (loggableErrors.length > 0) {
        const message = `Monolith PayPal pay endpoint request failed: ${e.message}`;
        logger.error(
          {
            err: loggableErrors,
            context: 'PayPalExpress/handlers/handleOnApprove',
            message,
          },
          message
        );
      }

      // We can further parse errors that satisfy "PayAPIError" to extract messages that the
      // Monolith generated (e.g. "Express shipping isn't available for P.O boxes").
      if (e instanceof PayAPIError) {
        const { errors } = e.payload;
        // A "PayAPIError" should never have an empty error list but extra safety never hurts <3
        if (errors.length > 0) {
          const structuredError = getPaymentErrorMessage(errors[0]);
          // At this point we will have a properly mapped error message, so we hand it back to
          // the error callback (which does the UI display) and hand back "authResponse" and a
          // blank thanks URL. The caller (handleOnApprove below) will detect the blank URL and
          // avoid triggering the generic PayPal "onError" callback.
          // If "onError" was triggered, this mapped error would be overridden by the generic
          // error message in "PayPalExpress.onError".
          onError?.(structuredError);
          return null;
        }
      }

      // At this point we have determined that the error is from code we do not control,
      // meaning we cannot parse it into anything meaningful. We re-throw the error here
      // so that the PayPal "onError" handler catches it. To avoid leaking sensitive information
      // within the error, the handler displays a generic error to the customer.
      throw e;
    });

  return {
    thankYouPageUrl,
    authResponse,
  };
};

export const handleOnApprove = async ({
  paymentData,
  rbEmail,
  clientDeviceData,
  paypalClient,
  onError,
  page,
  shippingOverride,
  billingOverride,
  consent,
}: HandleOnApproveParams): Promise<AuthorizationResponse> => {
  const approveResult = await doApprove({
    paypalClient,
    paymentData,
    rbEmail,
    clientDeviceData,
    onError,
    page,
    shippingOverride,
    billingOverride,
    consent,
  });
  const { authResponse, thankYouPageUrl } = approveResult;

  // A null URL indicates that we will display an error message from the Monolith.
  // (see the above error handling comments). So we don't redirect and instead hand
  // back the auth response to the PayPal SDK to keep it happy.
  if (thankYouPageUrl !== null) window.location.assign(thankYouPageUrl);

  return authResponse;
};
